import React, { ReactNode, useCallback } from "react";
import { connect } from "react-redux";

import Tag from "antd/es/tag";
import cn from "classnames";
import type { CustomTagProps } from "rc-select/lib/BaseSelect";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import defaultFilterOption from "@mapmycustomers/shared/util/input/defaultFilterOption";
import { SelectField, SelectFieldProps } from "@mapmycustomers/ui";

import { getActivityTypes } from "@app/store/activity";
import { RootState } from "@app/store/rootReducer";
import useActivityTypeOptions from "@app/util/ui/useActivityTypeOptions";

import styles from "./MultiSelectActivityTypeField.module.scss";

interface MultiSelectActivityTypeFieldProps extends Omit<
  SelectFieldProps<Array<ActivityType["id"]>>,
  "onChange" | "value"
> {
  label?: ReactNode;
  onChange?: (value: ActivityType["id"][]) => void;
  specificActivityTypes?: ActivityType[];
  value?: ActivityType["id"][];
}

const MultiSelectActivityTypeField = ({
  activityTypes,
  label,
  onChange,
  specificActivityTypes,
  value,
  ...props
}: MultiSelectActivityTypeFieldProps & ReturnType<typeof mapStateToProps>) => {
  const activityTypeOptions = useActivityTypeOptions(
    specificActivityTypes ?? activityTypes,
    value,
    useCallback((type: ActivityType) => {
      return !type.active;
    }, [])
  );
  const { size } = props;

  function tagRender(props: CustomTagProps) {
    const { label, onClose } = props;

    return (
      <Tag
        className={cn(styles.tag, { [styles.largeField]: size === "large" })}
        closable
        onClose={onClose}
      >
        {label}
      </Tag>
    );
  }

  return (
    <SelectField<Array<ActivityType["id"]>>
      dropdownMatchSelectWidth={false}
      filterOption={defaultFilterOption}
      label={label}
      maxTagCount="responsive"
      mode="multiple"
      onChange={onChange}
      options={activityTypeOptions}
      showSearch
      tagRender={tagRender}
      value={value}
      {...props}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  activityTypes: getActivityTypes(state),
});

export default connect(mapStateToProps, {})(MultiSelectActivityTypeField);
